import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert/quick-insert-plugin-type';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { Selection } from '@atlaskit/editor-prosemirror/state';
import { safeInsert } from '@atlaskit/editor-prosemirror/utils';

import { INSERT_SNIPPET_FAILURE_REASON } from '../../constants/snippetAnalytics.ts';
import { reportError } from '../../utils/reportError.ts';
import type { Snippet } from '../../types/snippet.ts';

/**
 * Outcome of an attempt to insert a snippet.
 *
 * The statuses are deliberately distinct so callers can separate benign cases
 * from genuine insert failures, and — among the benign cases — a transient,
 * retriable state from a terminal one:
 *
 * - `not-ready` is **transient/retriable**: the editor API is still
 *   initialising. A later attempt may succeed, so callers should keep retrying
 *   (and must not clean up any pending state such as a URL param).
 * - `empty-body` is **terminal**: the snippet has no content to insert. This
 *   will never change on retry, so callers should give up and clean up.
 *
 * Only `error` represents a real failure that should be surfaced as a `failed`
 * analytics event.
 */
export type InsertSnippetResult =
	| { status: 'success' }
	/** Editor not ready (timing) — benign and retriable, not a failure. */
	| { status: 'not-ready' }
	/** Snippet had no body — benign but terminal (will not change on retry). */
	| { status: 'empty-body' }
	/** A genuine failure: an exception was thrown during insert. */
	| { status: 'error'; reason: INSERT_SNIPPET_FAILURE_REASON };

type EditorAPIWithPreservedSelection = PublicPluginAPI<[QuickInsertPlugin]> & {
	blockControls?: {
		commands?: {
			stopPreservingSelection?: () => ({ tr }: { tr: unknown }) => unknown;
		};
		sharedState?: {
			currentState?: () => { preservedSelection?: Selection } | undefined;
		};
	};
};

const getInsertPositionAfterSelection = (selection?: Selection): number | undefined => {
	if (!selection) {
		return undefined;
	}

	const blockRange = selection.$from.blockRange(selection.$to);
	return blockRange?.end ?? selection.to;
};

const getPreservedSelection = (
	editorAPI: EditorAPIWithPreservedSelection,
	shouldUsePreservedSelection?: boolean,
): Selection | undefined => {
	if (!shouldUsePreservedSelection) {
		return undefined;
	}

	return editorAPI.blockControls?.sharedState?.currentState?.()?.preservedSelection;
};

/**
 * Hook for inserting a snippet at the current cursor position in the editor.
 * Pass editorAPI from the caller (e.g. useFullPageEditorApiState or EditorContextPanelContext).
 *
 * @returns A function to insert a snippet. The result distinguishes a successful
 *   insert, a benign not-ready case (editor API unavailable or empty body), and a
 *   genuine failure (an exception thrown during insert).
 */
export const useInsertSnippet = (editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null) => {
	return (snippet: Snippet, insertBelowPreservedSelection = false): InsertSnippetResult => {
		if (!editorAPI?.core?.actions?.execute) {
			// Editor not ready (timing) — benign, not a real failure.
			return { status: 'not-ready' };
		}

		const body = snippet.body;
		if (!body) {
			// Snippet has no body to insert — benign data issue, not a real failure.
			// Terminal: unlike not-ready, this will not change on retry, so callers
			// should give up and clean up rather than retry indefinitely.
			return { status: 'empty-body' };
		}

		const editorAPIWithPreservedSelection = editorAPI as EditorAPIWithPreservedSelection;
		const preservedSelection = getPreservedSelection(
			editorAPIWithPreservedSelection,
			insertBelowPreservedSelection,
		);

		try {
			editorAPI.core.actions.execute(({ tr }: { tr: any }) => {
				const schema = tr.doc.type.schema;

				// Convert snippet body to ProseMirror content
				// Body can be either a single ADFEntity or an array of ADFEntity
				const content = Array.isArray(body)
					? Fragment.fromJSON(schema, body)
					: schema.nodeFromJSON(body);

				const insertPosition = getInsertPositionAfterSelection(preservedSelection);
				const nextTr = safeInsert(content, insertPosition, insertPosition === undefined)(tr) ?? tr;

				if (preservedSelection) {
					editorAPIWithPreservedSelection.blockControls?.commands?.stopPreservingSelection?.()({
						tr: nextTr,
					});
				}

				return nextTr;
			});

			return { status: 'success' };
		} catch (error) {
			reportError(error);
			return { status: 'error', reason: INSERT_SNIPPET_FAILURE_REASON.INSERT_EXCEPTION };
		}
	};
};
